import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import connectToStores from 'alt-utils/lib/connectToStores';
import {List, Map, Set} from 'immutable';
import UserStore from 'stores/UserStore';
import DashMetricsStore, {MetricKey} from 'stores/dashboard/DashMetricsStore';
import debounce from 'lodash/debounce';
import UserActions from 'actions/UserActions';
import moment from 'moment';
import Team from 'records/Team';
import mapValues from 'lodash/mapValues';
import StatTab from 'components/dashboard/StatTab';
import StatTabContent from 'components/dashboard/StatTabContent';
import Loading from 'components/pure/utils/Loading';
import Tabs from 'components/pure/utils/Tabs';
import Theme from 'styles/theme';
import {RollupConfig} from 'components/dashboard/rollups/rollupTypes';
import checkFields from 'utils/checkFields';
import ExecDashboardStore from 'stores/ExecDashboardStore';
import AuthStore from 'stores/AuthStore';
import DirectReportsStore from 'stores/coaching/DirectReportsStore';
import {StatType} from 'sources/dashboard/DashMetricsSource';
import DashBlacklistStore from 'stores/dashboard/DashBlacklistStore';
import DashBlacklistActions from 'actions/dashboard/DashBlacklistActions';
import {hasPermission, Permission} from 'utils/hasPermission';
import {HISTORY_ACTIONS} from 'components/StatsPage2.utils';
import {RepProfileTab} from '@store/coaching-plans';
import {mapTabIndexToPageName} from 'components/dashboard/rep/RepProfilePageContainer';
import {getCoachRepLink} from 'components/dashboard/coach/CoachSummaryDashboard';
import Link from 'react-router/lib/Link';

@connectToStores
export default class DashboardRollup extends React.PureComponent {
  usersToFetch = Set();

  static getStores() {
    return [
      UserStore,
      DashMetricsStore,
      DirectReportsStore,
      ExecDashboardStore,
      AuthStore,
      DashBlacklistStore,
    ];
  }

  static getPropsFromStores({config}) {
    const {teams, directReportsLoading} = DirectReportsStore.getState();
    const {teamsInAccount, teamsInAccountLoading} = ExecDashboardStore.getState();
    return {
      blacklist: DashBlacklistStore.getState().userIds,
      users: UserStore.getState().users,
      teams,
      allTeams: teamsInAccount,
      teamsLoading: directReportsLoading || teamsInAccountLoading,
      sessionUser: AuthStore.getState().user,
      metrics: config.isUserBased
        ? DashMetricsStore.getState().user
        : DashMetricsStore.getState().team,
      chartLabelFn: config.isUserBased
        ? (chartTitle, name) => `${chartTitle} ${name}`
        : (chartTitle, name) => `${chartTitle} - ${name}`,
    };
  }

  constructor(props) {
    super(props);
    this.state = {
      activeTabId: props.config.defaultTab,
      activeSubtabId: props.config.defaultTab,
      tabChanged: false,
      secondaryDimensionFilterId: undefined,
    };

    // We fetch users after each metric call comes back. Since we can send lots of these
    // at once, make sure we aren't spamming the user endpoint.
    this.fetchUsers = debounce(() => {
      UserActions.fetchAll.defer({ids: this.usersToFetch});
    }, 100);

    this.fetchTrendData = this.fetchTrendData.bind(this);
    this.getLinkAndName = this.getLinkAndName.bind(this);
  }

  requestData() {
    const {startDate, endDate, config} = this.props;
    const teamIds = this.getTeamIds();
    const selectedMetric = this.getTabId();

    // Requests will 500 if we don't have any teamIds so avoid that
    if (teamIds.isEmpty()) return;

    DashBlacklistActions.fetchBlacklist.defer({
      teams: teamIds,
      startDate,
      endDate,
    });

    // Avoiding dispatch in dispatch here
    setTimeout(async () => {
      await config.fetchMetricData(selectedMetric, {
        teamIds,
        direction: config.direction,
        startDate,
        endDate,
        stat: StatType.per,
      });

      for (const metric of config.tabMetrics) {
        if (metric === selectedMetric) continue;
        config.fetchMetricData.defer(metric, {
          teamIds,
          direction: config.direction,
          startDate,
          endDate,
          stat: StatType.per,
        });
      }
    }, 0);
  }

  componentDidMount() {
    this.requestData();
  }

  componentDidUpdate(props) {
    const {metrics, users} = this.props;
    const apiProps = ['startDate', 'endDate', 'selectedTeam', 'teams', 'allTeams'];
    if (checkFields(this.props, props, apiProps)) {
      this.requestData();
    }

    if (metrics && !metrics.equals(props.metrics)) {
      // fetch new users that we don't already have
      const newUserIds = metrics
        .valueSeq()
        .flatten(1)
        .filter(x => x)
        .map(stat => stat.id)
        .toSet()
        .filter(id => !users.has(id));
      this.usersToFetch = this.usersToFetch.union(newUserIds);
      this.fetchUsers();
    }
  }

  getTabId() {
    const {location} = this.props;
    const {activeTabId: activeTabIdInState, tabChanged} = this.state;

    const tabIdFromReferer = location && location.state && location.state.activeTabId;

    if (tabIdFromReferer && !tabChanged) {
      return tabIdFromReferer;
    } else {
      return activeTabIdInState;
    }
  }

  getSubtabId() {
    const {location} = this.props;
    const {activeSubtabId: activeSubtabIdInState, tabChanged} = this.state;

    const subtabIdFromReferer = location && location.state && location.state.activeSubtabId;

    if (subtabIdFromReferer && !tabChanged) {
      return subtabIdFromReferer;
    } else {
      return activeSubtabIdInState;
    }
  }

  getTeamIds() {
    const {
      managerId,
      sessionUser,
      coachId,
      allTeams = Map(),
      teams = List(),
      selectedTeam,
      teamsLoading,
    } = this.props;
    if (teamsLoading) return Set();

    // If we have a team selected, just return that.
    if (selectedTeam && selectedTeam.id) {
      return Set([selectedTeam.id]);
    }

    const noCoachOrManager = !coachId && !managerId;
    const canViewDeptStats = hasPermission(sessionUser, Permission.DEPT_STATS);
    const isAdmin = sessionUser.isAdmin();
    // If a user is an admin or can view other departments stats, then show all teams. The payload is limited
    // to ones in their department cookie
    // Don't do this if we're viewing a coach or manager's dash
    if (noCoachOrManager && (canViewDeptStats || isAdmin)) {
      return allTeams
        .valueSeq()
        .map(t => t.id)
        .toSet();
    }

    return teams.map(t => t.id).toSet();
  }

  /**
   * Get trend data about the stat from the server.
   * @param stat the stat to get a trend for. Must have an id property (the id of either a team or
   * a user that we want a trend line for).
   */
  fetchTrendData(stat) {
    const {activeTabId, activeSubtabId} = this.state;
    const {startDate, endDate, config} = this.props;
    const type = activeSubtabId || activeTabId;
    const {direction} = config;
    const params = {direction, startDate, endDate, type};
    config.fetchTrendData(stat, params);
  }

  getLinkAndName(stat) {
    // TODO Try to reduce the complexity from these functions
    const {users, startDate, endDate, adminId, managerId, location, allTeams} = this.props;
    const {linkDestination, direction} = this.props.config;
    if (this.props.config.isUserBased) {
      return getUserBasedLinkAndName(
        stat,
        users,
        startDate,
        endDate,
        adminId,
        users.get(managerId),
        location.pathname,
        linkDestination,
        direction
      );
    } else {
      return getExecTeamBasedLinkAndName(
        stat,
        adminId,
        allTeams,
        location.pathname,
        linkDestination,
        {activeTabId: this.state.activeTabId, activeSubtabId: this.state.activeSubtabId}
      );
    }
  }

  render() {
    const activeSubtabId = this.getSubtabId();
    const {
      metrics,
      startDate,
      endDate,
      headerStartDate,
      headerEndDate,
      users,
      location,
      config,
      teamsLoading,
      managerId,
      chartLabelFn,
      blacklist,
    } = this.props;
    const {
      className,
      tabConfigs,
      tabMetrics,
      direction,
      descriptionsFn,
      trendComponent,
      fetchMetricSource,
    } = config;

    const activeTabId = this.getTabId();
    const teamIds = this.getTeamIds();

    if (teamsLoading) return <Loading />;
    if (teamIds.isEmpty()) {
      return (
        <div className={cn('no-teams-error', 'dashv2-widget', className)}>
          Couldn't find any applicable teams to report on.
        </div>
      );
    }

    // Even if there's, no metric data we still return empty rows for each team.
    // So as long as we have teams, we don't need to worry about hanging here.
    if (metrics.isEmpty()) return <Loading />;

    const tabRenderers = mapValues(tabConfigs, config => values => (
      <StatTab tabConfig={config} values={values} />
    ));

    const stats = Map(
      tabMetrics.map(metric => {
        let data = metrics.get(new MetricKey(metric, teamIds, direction)) || List();
        if (config.isUserBased) {
          data = data.filterNot(s => blacklist.contains(s.id));
        }
        return [metric, data];
      })
    );
    const manager = users.get(managerId);

    return (
      <div className={`dashv2-widget ${className}`}>
        <Tabs
          onUpdateTab={activeTabId =>
            this.setState({
              activeTabId,
              activeSubtabId: activeTabId,
              tabChanged: true,
            })
          }
          activeTabId={activeTabId}
          tabRenderers={tabRenderers}
          stats={stats}
        />
        <StatTabContent
          key={activeTabId}
          color={Theme.palette.smoky}
          tabConfigs={tabConfigs}
          activeTabId={activeTabId}
          stats={stats.get(activeSubtabId)}
          getLinkAndName={this.getLinkAndName}
          fetchTrendData={this.fetchTrendData}
          fetchMetricSource={fetchMetricSource}
          startDate={startDate}
          endDate={endDate}
          headerStartDate={headerStartDate}
          headerEndDate={headerEndDate}
          direction={direction}
          teamIds={teamIds}
          location={location}
          selectSubtab={tab => this.setState({activeSubtabId: tab})}
          activeSubtabId={activeSubtabId}
          descriptions={descriptionsFn({manager})}
          trendComponent={trendComponent}
          chartLabelFn={chartLabelFn}
        />
      </div>
    );
  }
}

DashboardRollup.propTypes = {
  config: PropTypes.instanceOf(RollupConfig).isRequired,

  // dashv2 props
  selectedTeam: PropTypes.instanceOf(Team),
  location: PropTypes.object,
  startDate: PropTypes.instanceOf(moment),
  endDate: PropTypes.instanceOf(moment),
};

const getExecTeamBasedLinkAndName = (
  stat,
  adminId,
  teamsInAccount,
  fromPath,
  page = null,
  state = {}
) => {
  const team = teamsInAccount && teamsInAccount.get(stat.id);

  if (!team) return [];

  const extraState = fromPath
    ? {
        pathname: fromPath,
        action: HISTORY_ACTIONS.PUSH,
      }
    : {};
  extraState.selectedTeam = team;
  const newState = Object.assign(state, extraState);
  const managerPage = page ? page : 'summary';
  return [
    <Link
      to={{
        pathname: `/dashv2/admin/${adminId}/manager/${team.managerId}/${managerPage}`,
        state: newState,
      }}
      className={'generic-link'}>
      {team.name}
    </Link>,
    team.name,
  ];
};

const getUserBasedLinkAndName = (
  stat,
  users,
  startDate,
  endDate,
  adminId,
  manager,
  fromPath,
  tabIndex,
  coachDirection
) => {
  if (!stat) return [];

  const user = users && users.get(stat.id);

  if (!user) return [];

  if (user.isTeamManager) {
    return getManagerLinkAndName(user, startDate, endDate, adminId, manager, fromPath, tabIndex);
  } else {
    return getRepOrCoachLinkAndName(
      user,
      startDate,
      endDate,
      adminId,
      manager,
      fromPath,
      tabIndex,
      coachDirection
    );
  }
};

const getManagerLinkAndName = (user, startDate, endDate, adminId, manager, fromPath, tabIndex) => {
  const extraState = fromPath
    ? {
        pathname: fromPath,
        action: HISTORY_ACTIONS.PUSH,
      }
    : {};

  const page = tabIndex === RepProfileTab.REVIEW_ACTIVITIES ? 'your-coaching' : 'summary';

  return [
    user.active ? (
      <Link
        to={{
          pathname: `/dashv2/admin/${adminId}/manager/${user.id}/${page}`,
          state: extraState,
        }}
        className={'generic-link'}>
        {user.getFullName()}
      </Link>
    ) : (
      <span>{user.getFullName()}</span>
    ),
    user.firstNamePossessive,
  ];
};

const getRepOrCoachLinkAndName = (
  user,
  startDate,
  endDate,
  adminId,
  manager,
  fromPath,
  tabIndex,
  coachDirection
) => {
  let link = {};
  const props = {
    adminId,
    managerId: manager ? manager.id : null,
    startDate,
    endDate,
    fromPath,
  };
  const query = {};
  if (tabIndex) {
    query.tab = tabIndex;
  }
  if (coachDirection) {
    query.direction = coachDirection;
  }
  link.query = query;
  const page = mapTabIndexToPageName(tabIndex, coachDirection);

  link = getCoachRepLink({rep: user}, props, link, true, page);

  const name = user.active ? (
    <Link
      to={{
        pathname: link.pathname,
        query: user.is_coach ? {} : link.query,
        state: link.state,
      }}
      className={'generic-link'}>
      {user.getFullName()}
    </Link>
  ) : (
    <span>{user.getFullName()}</span>
  );

  return [name, user.firstNamePossessive];
};
